import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { getCountryName } from '../../../lib/countries';

const countryCode = (import.meta.env.VITE_COUNTRY_CODE as string) || '';
const countryName = getCountryName(countryCode);

function validatePassword(pwd: string): string | null {
  if (pwd.length < 8) return 'Password must be at least 8 characters';
  if (!/[A-Z]/.test(pwd)) return 'Password must contain at least one uppercase letter';
  if (!/[a-z]/.test(pwd)) return 'Password must contain at least one lowercase letter';
  if (!/[0-9]/.test(pwd)) return 'Password must contain at least one number';
  return null;
}

export default function ResetPasswordPage() {
  const { user, updatePassword, supabase } = useAuth();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);

  useEffect(() => {
    async function checkSession() {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) {
        setError('Invalid or expired reset link. Please request a new one.');
      } else if (!session) {
        setError('No active session. Please request a new reset link.');
      }
      setSessionChecked(true);
    }
    checkSession();
  }, [supabase]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const validationError = validatePassword(password);
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      await updatePassword(password);
      setSuccess(true);
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  }

  if (!sessionChecked) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="login-logo">CleanMyBed</div>
            <div className="login-subtitle">{countryName}</div>
          </div>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p className="text-muted">Verifying reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">CleanMyBed</div>
          <div className="login-subtitle">{countryName}</div>
        </div>

        {success ? (
          <div>
            <div className="alert alert-success mb-lg">
              <p className="m-0 fw-500">
                Password reset successfully!
              </p>
            </div>
            <p style={{
              color: 'var(--color-text-light)',
              fontSize: 'var(--text-base)',
              textAlign: 'center',
            }}>
              Redirecting to login...
            </p>
          </div>
        ) : error && !user ? (
          <div>
            <div className="alert alert-danger mb-lg">
              <p className="m-0 fw-500">
                {error}
              </p>
            </div>
            <Link
              to="/forgot-password"
              className="btn btn-primary"
              style={{ width: '100%', display: 'block', textAlign: 'center', textDecoration: 'none' }}
            >
              Request New Reset Link
            </Link>
            <div className="mt-lg text-center text-sm">
              <Link to="/login" className="text-primary">Back to Login</Link>
            </div>
          </div>
        ) : (
          <>
            <p style={{
              color: 'var(--color-text-light)',
              fontSize: 'var(--text-base)',
              textAlign: 'center',
              marginBottom: '1.5rem',
            }}>
              Enter your new password below.
            </p>

            <form onSubmit={handleSubmit}>
              {error && (
                <div className="form-group">
                  <div className="form-error text-center">{error}</div>
                </div>
              )}

              <div className="form-group">
                <label className="form-label" htmlFor="password">New Password</label>
                <input
                  id="password"
                  type="password"
                  className="form-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your new password"
                  required
                  disabled={loading}
                  minLength={8}
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="confirmPassword">Confirm Password</label>
                <input
                  id="confirmPassword"
                  type="password"
                  className="form-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your new password"
                  required
                  disabled={loading}
                  minLength={8}
                />
              </div>

              <div style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--color-text-light)',
                marginBottom: '1rem',
                lineHeight: '1.5',
              }}>
                Password must be at least 8 characters and contain uppercase, lowercase, and a number.
              </div>

              <button
                type="submit"
                className="btn btn-primary w-full mt-sm"
                disabled={loading}
              >
                {loading ? 'Resetting Password...' : 'Reset Password'}
              </button>
            </form>
          </>
        )}

        <div className="login-footer">
          &copy; {new Date().getFullYear()} Sentinel&trade; Business Management
        </div>
      </div>
    </div>
  );
}
